// src/pages/ProfilePage.js
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./ProfilePage.css";
import { API_URL } from "../config";

/* Helpers */
const safeStr = (v) => (v === null || v === undefined ? "" : String(v));

function getInitials(firstName, lastName) {
  const f = safeStr(firstName).trim()[0] || "";
  const l = safeStr(lastName).trim()[0] || "";
  return `${f}${l}`.toUpperCase() || "U";
}

// „Losowy” kolor, ale stabilny dla użytkownika (żeby nie skakał co odświeżenie)
function stableHueFromString(input) {
  let hash = 0;
  const s = safeStr(input);
  for (let i = 0; i < s.length; i++) {
    hash = (hash * 31 + s.charCodeAt(i)) % 360;
  }
  return hash;
}

function formatDatePL(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("pl-PL");
}

// prosty staż: X lat Y mies.
function formatTenure(hireDateStr) {
  if (!hireDateStr) return "—";
  const start = new Date(hireDateStr);
  if (Number.isNaN(start.getTime())) return "—";

  const now = new Date();

  let months =
    (now.getFullYear() - start.getFullYear()) * 12 +
    (now.getMonth() - start.getMonth());

  // jeśli dzień miesiąca jeszcze nie „minął”, odejmij 1 miesiąc
  if (now.getDate() < start.getDate()) months -= 1;
  if (months < 0) months = 0;

  const years = Math.floor(months / 12);
  const remMonths = months % 12;

  const parts = [];
  if (years > 0) parts.push(`${years} ${years === 1 ? "rok" : years < 5 ? "lata" : "lat"}`);
  if (remMonths > 0)
    parts.push(
      `${remMonths} ${
        remMonths === 1 ? "miesiąc" : remMonths < 5 ? "miesiące" : "miesięcy"
      }`
    );

  return parts.length ? parts.join(" ") : "mniej niż miesiąc";
}

function InfoRow({ label, value }) {
  return (
    <div className="pf-row">
      <div className="pf-row-label">{label}</div>
      <div className="pf-row-value">{value ?? "—"}</div>
    </div>
  );
}

function ProfilePage() {
  const navigate = useNavigate();

  const [profile, setProfile] = useState(() => {
    // szybki start z localStorage
    try {
      const raw = localStorage.getItem("user");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });

  const [loading, setLoading] = useState(!profile);
  const [error, setError] = useState("");

  // odśwież dane z backendu (sesja)
  useEffect(() => {
    let isMounted = true;

    const fetchSession = async () => {
      setError("");
      try {
        const res = await fetch(`${API_URL}/checkSession`, {
          credentials: "include",
        });

        if (!res.ok) {
          // jeśli brak sesji, nie wywalaj błędu na czerwono — route i tak zwykle jest za auth
          if (isMounted) {
            setProfile(null);
            setLoading(false);
          }
          return;
        }

        const data = await res.json();
        if (isMounted) {
          setProfile(data?.user || null);
          if (data?.user) localStorage.setItem("user", JSON.stringify(data.user));
        }
      } catch (e) {
        if (isMounted) setError("Nie udało się pobrać danych profilu.");
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchSession();
    return () => {
      isMounted = false;
    };
  }, []);

  const fullName = useMemo(() => {
    if (!profile) return "—";
    return `${safeStr(profile.first_name)} ${safeStr(profile.last_name)}`.trim() || "—";
  }, [profile]);

  const initials = useMemo(() => {
    if (!profile) return "U";
    return getInitials(profile.first_name, profile.last_name);
  }, [profile]);

  const avatarHue = useMemo(() => {
    const key = profile ? `${profile.id || ""}-${profile.login || ""}-${profile.email || ""}` : "guest";
    return stableHueFromString(key);
  }, [profile]);

  const avatarStyle = useMemo(() => {
    // lekkie „glass/gradient” tylko w avatarze
    return {
      background: `linear-gradient(135deg,
        hsla(${avatarHue}, 80%, 55%, 0.92),
        hsla(${(avatarHue + 35) % 360}, 80%, 45%, 0.92)
      )`,
    };
  }, [avatarHue]);

  if (loading) {
    return (
      <section className="profile-page">
        <div className="pf-container">
          <div className="pf-skeleton-card" />
          <div className="pf-skeleton-grid">
            <div className="pf-skeleton-box" />
            <div className="pf-skeleton-box" />
            <div className="pf-skeleton-box" />
          </div>
        </div>
      </section>
    );
  }

  if (!profile) {
    return (
      <section className="profile-page">
        <div className="pf-container">
          <div className="pf-empty">
            <h2>Mój profil</h2>
            <p>Brak danych użytkownika (prawdopodobnie sesja wygasła).</p>

            <div className="pf-actions">
              <button className="pf-btn pf-btn-primary" onClick={() => navigate("/login")}>
                Przejdź do logowania
              </button>
            </div>

            {error && <p className="pf-error">{error}</p>}
          </div>
        </div>
      </section>
    );
  }

  const isActive = !profile.departure_date;

  return (
    <section className="profile-page">
      <div className="pf-container">
        {/* HERO */}
        <div className="pf-hero">
          <div className="pf-avatar" style={avatarStyle} aria-hidden>
            {initials}
          </div>

          <div className="pf-hero-text">
            <h2 className="pf-title">Mój profil</h2>

            <div className="pf-name">{fullName}</div>

            <div className="pf-chips">
              <span className="pf-chip">
                {profile.role || "User"}
              </span>
              <span className="pf-chip pf-chip-muted">
                {profile.position || "—"}
              </span>
              <span className={`pf-chip ${isActive ? "pf-chip-ok" : "pf-chip-warn"}`}>
                {isActive ? "Aktywne" : "Nieaktywne"}
              </span>
            </div>

            <div className="pf-meta">
              <span className="pf-meta-item">
                <span className="pf-meta-label">Login:</span> {profile.login}
              </span>
              <span className="pf-meta-dot">•</span>
              <a className="pf-meta-link" href={`mailto:${profile.email}`}>
                {profile.email}
              </a>
            </div>
          </div>

          <div className="pf-hero-actions">
            <button className="pf-btn pf-btn-ghost" onClick={() => navigate("/settings")}>
              Ustawienia
            </button>
          </div>
        </div>

        {error && <p className="pf-error" role="alert">{error}</p>}

        {/* GRID */}
        <div className="pf-grid">
          <div className="pf-card">
            <div className="pf-card-title">Dane konta</div>
            <InfoRow label="ID użytkownika" value={profile.id} />
            <InfoRow label="Login" value={profile.login} />
            <InfoRow
              label="E-mail"
              value={<a className="pf-link" href={`mailto:${profile.email}`}>{profile.email}</a>}
            />
          </div>

          <div className="pf-card">
            <div className="pf-card-title">Informacje pracownicze</div>
            <InfoRow label="Rola" value={profile.role || "—"} />
            <InfoRow label="Stanowisko" value={profile.position || "—"} />
            <InfoRow label="Zatrudniony od" value={formatDatePL(profile.hire_date)} />
            <InfoRow label="Staż" value={formatTenure(profile.hire_date)} />
          </div>

          <div className="pf-card">
            <div className="pf-card-title">Daty</div>
            <InfoRow label="Data urodzenia" value={formatDatePL(profile.birth_date)} />
            <InfoRow
              label="Data odejścia"
              value={profile.departure_date ? formatDatePL(profile.departure_date) : "—"}
            />
            <InfoRow
              label="Pierwsze logowanie"
              value={profile.first_login ? "Tak (wymagana zmiana hasła)" : "Nie"}
            />
          </div>
        </div>

        {/* QUICK NOTE */}
        <div className="pf-note">
          <div className="pf-note-title">Wskazówka</div>
          <div className="pf-note-text">
            Jeśli chcesz zmienić hasło lub ustawienia konta, przejdź do{" "}
            <button className="pf-link-btn" type="button" onClick={() => navigate("/settings")}>
              Ustawień
            </button>.
          </div>
        </div>
      </div>
    </section>
  );
}

export default ProfilePage;
